/**
 * Phase 4 slice 3 — Quality Heatmap. Leadership view: where are our quality
 * problems concentrated? Four rankings from /quality/heatmap.
 */
import React, { useEffect, useState } from 'react';
import { MapPin, Layers, CalendarDays, GitBranch } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import { apiGet } from '../../lib/api';
import { toast } from 'sonner';

const Bars = ({ items, labelKey = 'key', tone = '#3b4872', testid }) => {
  const max = Math.max(1, ...items.map((i) => i.count));
  return (
    <div className="space-y-2" data-testid={testid}>
      {items.length === 0 ? <div className="text-xs text-slate-400 py-4 text-center">No data.</div>
        : items.map((it, i) => (
          <div key={i}>
            <div className="flex justify-between text-xs mb-0.5">
              <span className="text-slate-700 truncate max-w-[220px]">{it[labelKey]}</span>
              <span className="font-display font-bold text-slate-800 tabular-nums">{it.count}</span>
            </div>
            <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
              <div className="h-full rounded-full transition-all" style={{ width: `${(it.count / max) * 100}%`, backgroundColor: tone }} />
            </div>
          </div>
        ))}
    </div>
  );
};

const Panel = ({ icon: Icon, title, children }) => (
  <div className="bg-white border border-slate-200 rounded-sm p-4">
    <div className="kpi-overline mb-3 flex items-center gap-2"><Icon size={12} /> {title}</div>
    {children}
  </div>
);

export default function QualityHeatmap() {
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState('');
  const [data, setData] = useState(null);

  useEffect(() => { apiGet('/projects').then(setProjects).catch(() => toast.error('Failed to load projects')); }, []);
  useEffect(() => {
    apiGet(`/quality/heatmap${projectId ? `?project_id=${projectId}` : ''}`).then(setData).catch(() => {});
  }, [projectId]);

  const monthItems = (data?.by_month || []).map((m) => ({ key: m.month, count: m.count }));

  return (
    <div>
      <PageHeader overline="Quality" title="Quality Heatmap" subtitle="Where quality problems concentrate — by location, type, time and root cause" />

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <select value={projectId} onChange={(e) => setProjectId(e.target.value)} className="px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white min-w-[240px]" data-testid="heatmap-project-select">
          <option value="">All projects</option>
          {projects.map((p) => <option key={p.id} value={p.id}>{p.code ? `${p.code} · ${p.name}` : p.name}</option>)}
        </select>
        <div className="flex gap-2 text-sm">
          <span className="px-3 py-2 bg-slate-100 rounded-sm" data-testid="heatmap-total">Total NCRs: <b>{data?.total_ncrs ?? 0}</b></span>
          <span className="px-3 py-2 bg-amber-50 text-amber-700 rounded-sm" data-testid="heatmap-open">Open: <b>{data?.open_ncrs ?? 0}</b></span>
          <span className="px-3 py-2 bg-red-50 text-red-700 rounded-sm" data-testid="heatmap-critical">Open high/critical: <b>{data?.open_high_critical ?? 0}</b></span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="quality-heatmap">
        <Panel icon={MapPin} title="Top Hotspots (by location)"><Bars items={data?.by_location || []} tone="#dc2626" testid="heatmap-locations" /></Panel>
        <Panel icon={Layers} title="By Category"><Bars items={data?.by_category || []} tone="#3b4872" testid="heatmap-categories" /></Panel>
        <Panel icon={CalendarDays} title="Trend (by month)"><Bars items={monthItems} tone="#0891b2" testid="heatmap-months" /></Panel>
        <Panel icon={GitBranch} title="Most-common Root Causes"><Bars items={data?.by_contributing_factor || []} tone="#d97706" testid="heatmap-factors" /></Panel>
      </div>
    </div>
  );
}
